import { SourceItem } from "../models/source-item.model";

/** Reserved stored-item name for the public session. StoreItem must not replace it. */
export const AUTHENTICATION_STORED_ITEM = "_authentication";

export interface AuthenticationIdentity {
    id: string;
    name: string;
    email: string;
    provider: string;
}

/**
 * Session fact for the item builder.
 * Omit `authenticated` when no session fact exists yet. That is the only case that sets `loading` to true.
 * The server seed and the client status write always pass `authenticated`.
 */
export interface AuthenticationItemInput {
    authenticated?: boolean;
    identity?: AuthenticationIdentity;
}

export interface AuthenticationSessionUser {
    userId?: unknown;
    _id?: { str?: unknown };
}

export interface AuthenticationStatusUser {
    id?: string;
    name?: string;
    email?: string;
    provider?: string;
}

function flag(value: boolean): [string] {
    return [value ? "true" : "false"];
}

/**
 * Build the `_authentication` property bag. Flags are the text `true` or `false` so an If can compare them.
 * A missing flag is not this builder's job: ItemProperty renders that as empty text, which is not `true`.
 */
export function buildAuthenticationItem(input: AuthenticationItemInput = {}): SourceItem {
    const hasSessionFact = typeof input.authenticated === "boolean";
    const authenticated = input.authenticated === true;
    const identity = authenticated ? input.identity : undefined;
    const provider = identity?.provider ?? "";
    const email = identity?.email ?? "";
    return {
        value: authenticated ? email : "",
        props: {
            system: {
                id: [identity?.id ?? ""],
                name: [identity?.name ?? ""],
                email: [email],
                provider: [provider],
                loading: flag(!hasSessionFact),
                authenticated: flag(hasSessionFact && authenticated),
                showLogin: flag(hasSessionFact && !authenticated),
                showLogout: flag(hasSessionFact && authenticated),
                canChangePassword: flag(hasSessionFact && authenticated && provider === "local"),
            },
        },
    };
}

/**
 * Session id from the access token. Production tokens carry `userId`. Some tests put `_id.str` on the payload.
 * `"guest"` and a missing id are signed out. Identity is not read from the token.
 */
export function resolveAuthenticationSessionId(user: AuthenticationSessionUser | null | undefined): string | null {
    if (!user) {
        return null;
    }
    const userId = typeof user.userId === "string" && user.userId.length > 0 ? user.userId : "";
    const legacyId = !userId && typeof user._id?.str === "string" && user._id.str.length > 0 ? user._id.str : "";
    const id = userId || legacyId;
    if (!id || id === "guest") {
        return null;
    }
    return id;
}

/** Map a completed status payload onto the stored item. A guest id is signed out. */
export function authenticationItemFromStatus(result: {
    isAuthenticated: boolean;
    user?: AuthenticationStatusUser | null;
}): SourceItem {
    const user = result.user;
    const id = typeof user?.id === "string" ? user.id : "";
    if (!result.isAuthenticated || id.length === 0 || id === "guest") {
        return buildAuthenticationItem({ authenticated: false });
    }
    return buildAuthenticationItem({
        authenticated: true,
        identity: {
            id,
            name: typeof user?.name === "string" ? user.name : "",
            email: typeof user?.email === "string" ? user.email : "",
            provider: typeof user?.provider === "string" ? user.provider : "",
        },
    });
}

/** True only when the stored flag's first value is the text `true`. */
export function authenticationFlag(item: SourceItem | undefined, name: string): boolean {
    const values = item?.props?.system?.[name];
    return Array.isArray(values) && values[0] === "true";
}

/** First string value of a system property, or empty when it is missing. */
export function authenticationProperty(item: SourceItem | undefined, name: string): string {
    const values = item?.props?.system?.[name];
    const first = Array.isArray(values) ? values[0] : "";
    return typeof first === "string" ? first : "";
}
